import type { Source, SourceId } from '@eformulieren/model'
import type * as Y from 'yjs'
import { addSource, removeSource, richtextFragment, rowFragment, rowsList, sourcesOf } from '../document/sectionDocument.js'
import { type CitationField, collectClustersFromFragment } from './collectClusters.js'
import type { NewSource } from './SourcePanel.js'

/**
 * De bronnen van een laag als één bibliotheek, en wat je ermee kunt (R-50, opdracht van 30 oktober 2026).
 *
 * Eén plek voor de kiezer onder een veld (`useSourcePicker`) en het paneel Bronnen in de kop
 * (`SourcesOverview`), zodat ze dezelfde bibliotheek tonen en een wijziging op dezelfde manier
 * schrijven. Een gekozen bron staat als kopie in elke sectie die hem citeert (ontwerp 6.8): wijzigen en
 * verwijderen gaan dus over alle kopieën, maar alleen in de secties die de lezer kan bewerken, want in
 * een andere sectie weigert de pod de schrijfactie.
 */

/** De bibliotheek: elke bron één keer, de eerste kopie in deze volgorde telt. */
export function layerLibrary(documents: ReadonlyMap<string, Y.Doc>, scope: readonly string[]): ReadonlyMap<string, Source> {
  const library = new Map<string, Source>()
  for (const id of scope) {
    const doc = documents.get(id)
    if (doc !== undefined) for (const source of sourcesOf(doc)) if (!library.has(source.id)) library.set(source.id, source)
  }
  return library
}

/** Elke kopie van deze bron in de laag, met of de lezer daar kan schrijven. */
function copiesOf(
  documents: ReadonlyMap<string, Y.Doc>,
  scope: readonly string[],
  canWrite: (sectionId: string) => boolean,
  sourceId: string,
): readonly { readonly doc: Y.Doc; readonly writable: boolean }[] {
  return [...new Set(scope)].flatMap((id) => {
    const doc = documents.get(id)
    return doc !== undefined && sourcesOf(doc).some((source) => source.id === sourceId) ? [{ doc, writable: canWrite(id) }] : []
  })
}

/**
 * Een bron wijzigen in elke kopie die de lezer kan bewerken. Geeft terug of er een kopie buiten bereik
 * bleef: dan zegt de aanroeper dat, want daar blijft de oude beschrijving staan.
 */
export function editSourceInLayer(
  documents: ReadonlyMap<string, Y.Doc>,
  scope: readonly string[],
  canWrite: (sectionId: string) => boolean,
  sourceId: string,
  changed: NewSource,
): { readonly buitenBereik: boolean } {
  const found = copiesOf(documents, scope, canWrite, sourceId)
  for (const copy of found) if (copy.writable) addSource(copy.doc, { ...changed, id: sourceId as SourceId })
  return { buitenBereik: found.some((copy) => !copy.writable) }
}

/**
 * Een bron verwijderen uit elke kopie die de lezer kan bewerken, en alleen als hij nergens in de tekst
 * van de laag staat: anders gaat een citatie naar niets wijzen. Geeft terug of er verwijderd is.
 */
export function removeSourceInLayer(
  documents: ReadonlyMap<string, Y.Doc>,
  scope: readonly string[],
  canWrite: (sectionId: string) => boolean,
  sourceId: string,
  inUse: ReadonlySet<string>,
): boolean {
  if (inUse.has(sourceId)) return false
  for (const copy of copiesOf(documents, scope, canWrite, sourceId)) if (copy.writable) removeSource(copy.doc, sourceId)
  return true
}

/** Waar een citatie staat: de sectie, het veld (of de herhaalgroep), en het id van de citatie in de tekst. */
export interface CitationPlace {
  readonly sectieId: string
  /** Het rich-text-veld, of bij een kolom in een herhaalgroep de herhaalgroep zelf. */
  readonly veldId: string
  readonly citatieId: string
}

/**
 * Per bron de plekken waar hij geciteerd wordt, in formuliervolgorde (R-50).
 *
 * Dezelfde wandeling als `collectClusters`, met het veld erbij: het paneel Bronnen telt hoe vaak een
 * bron in de tekst staat en springt naar die plek. Een citatie die twee bronnen noemt telt bij allebei.
 */
export function citationPlaces(
  documents: ReadonlyMap<string, Y.Doc>,
  sections: readonly { readonly sectieId: string; readonly fieldIds: readonly CitationField[] }[],
): ReadonlyMap<string, readonly CitationPlace[]> {
  const out = new Map<string, CitationPlace[]>()
  const note = (sectieId: string, veldId: string, clusters: ReturnType<typeof collectClustersFromFragment>): void => {
    for (const cluster of clusters) {
      for (const sourceId of new Set(cluster.bronnen)) {
        const list = out.get(sourceId) ?? []
        list.push({ sectieId, veldId, citatieId: cluster.citatieId })
        out.set(sourceId, list)
      }
    }
  }
  for (const { sectieId, fieldIds } of sections) {
    const doc = documents.get(sectieId)
    if (doc === undefined) continue
    for (const field of fieldIds) {
      if (typeof field === 'string') {
        note(sectieId, field, collectClustersFromFragment(richtextFragment(doc, field)))
        continue
      }
      for (const row of rowsList(doc, field.herhaalgroep).toArray()) {
        for (const column of field.kolommen) {
          const fragment = rowFragment(row, column)
          if (fragment !== undefined) note(sectieId, field.herhaalgroep, collectClustersFromFragment(fragment))
        }
      }
    }
  }
  return out
}
